import { ImmHelper } from "@base/util/Immutable";
import { getKeys, PartialRecord } from "@base/util/TypeUtil";
import { KederSailMountType, KederSize } from "@biz/definitions/KederDefinitions";
import { ConcavityType, EdgeName, EdgePositioningMode, EdgeSeamType, GrommetType, IEdgeData, IPatchData } from "@biz/definitions/SunSailDefinitions";

export function calcMaxKederPriority(edges?: PartialRecord<EdgeName, IEdgeData>): { edge: EdgeName, priority: number } | undefined {
  if (!edges)
    return undefined;
  let priority = -1;
  let edge: EdgeName | undefined;
  for (const k of getKeys(edges)) {
    const edgePriority = edges[k]?.kederSelectionPriority;
    if (edgePriority && edgePriority > priority) {
      priority = edgePriority
      edge = k;
    }
  }
  return edge ? { priority, edge } : undefined;
}

export function getEdgeReducers<T extends { edges?: PartialRecord<EdgeName, IEdgeData> }>(): {
  setConcavity(old: T, edgeName: EdgeName, concavity: ConcavityType | undefined): T;
  setKederSize(old: T, edgeName: EdgeName, kederSize: KederSize | undefined): T;
  setKederRail(old: T, edgeName: EdgeName, kederRail: string | undefined): T;
  setKederSailMountType(old: T, edgeName: EdgeName, kederSailMount: KederSailMountType | undefined): T;
  setSeamType(old: T, edgeName: EdgeName, seamType: EdgeSeamType | undefined): T;
  setGrommetType(old: T, edgeName: EdgeName, grommetType: GrommetType | undefined): T;
  setEdgePositioning(old: T, edgeName: EdgeName, edgePositioningMode: EdgePositioningMode | undefined): T;
  setEdgePositioningUserDist(old: T, edgeName: EdgeName, distributionDist: number | undefined): T;
  setEdgePositioningUserCount(old: T, edgeName: EdgeName, distributionCount: number | undefined): T;
} {
  return {
    setConcavity(old: T, edgeName, concavity) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "concavity", concavity)
    },
    setKederSize(old: T, edgeName, kederSize) {
      // const oldEdges = getKeys(old.edges || {});
      // if (oldEdges) {
      //   for (const oldEdgeName of oldEdges) {
      //     if (oldEdgeName !== edgeName)
      //       old = ImmHelper.setPropertyNonNullable(old, "edges", oldEdgeName, "kederSize", undefined)
      //   }
      // }
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "kederSize", kederSize)
    },
    setKederRail(old: T, edgeName, kederRail) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "kederRail", kederRail)
    },
    setKederSailMountType(old: T, edgeName, kederSailMount) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "kederSailMountType", kederSailMount)
    },
    setSeamType(old: T, edgeName, seamType) {
      old = ensureEdge(old, edgeName);
      if (seamType === "keder") {
        const priority = (calcMaxKederPriority(old.edges)?.priority || 0) + 1;
        old = ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "kederSelectionPriority", priority + 1)
      } else {
        old = ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "kederSelectionPriority", undefined)
      }
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "seamType", seamType)
    },
    setGrommetType(old: T, edgeName, grommetType) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "grommetType", grommetType)
    },
    setEdgePositioning(old, edgeName, edgePositioningMode) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "edgePositioning", edgePositioningMode)
    },
    setEdgePositioningUserDist(old, edgeName, dDistributionDist) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "edgePositioningUserDist", dDistributionDist)
    },
    setEdgePositioningUserCount(old, edgeName, distributionCount) {
      old = ensureEdge(old, edgeName);
      return ImmHelper.setPropertyNonNullable(old, "edges", edgeName, "edgePositioningUserCount", distributionCount)
    }
  }
}

export function ensureEdge<T extends { edges?: PartialRecord<EdgeName, IEdgeData> }>(old: T, edgeName: EdgeName): T {
  if (!old.edges)
    old = ImmHelper.setProperty(old, "edges", {})
  if (!old.edges![edgeName])
    old = ImmHelper.setPropertyNonNullable(old, "edges", edgeName, {})
  return old;
}

export function ensurePatches<T extends { patches?: IPatchData[] }>(old: T): T {
  if (!old.patches || !Array.isArray(old.patches))
    old = ImmHelper.setProperty(old, "patches", [])
  return old;
}

